


import { useAuth } from '../../context/AuthContext';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getDatabase, ref, onValue } from 'firebase/database';
import { motion } from 'framer-motion';
import { FiUser, FiLock, FiSmartphone } from 'react-icons/fi';

// Styles
const containerStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  padding: '20px',
  overflow: 'hidden',
  position: 'relative'
};

const floatingElements = {
  position: 'absolute',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  zIndex: 0
};

const floatingElement = (size, top, left, color, blur) => ({
  position: 'absolute',
  width: size,
  height: size,
  borderRadius: '50%',
  background: color,
  filter: `blur(${blur}px)`,
  top: `${top}%`,
  left: `${left}%`,
  opacity: 0.3
});

const formContainerStyle = {
  position: 'relative',
  zIndex: 1,
  width: '100%',
  maxWidth: '420px'
};

const formStyle = {
  width: '100%',
  padding: '40px',
  borderRadius: '24px',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  transition: 'all 0.3s ease',
  overflow: 'hidden'
};

const headerStyle = {
  textAlign: 'center',
  marginBottom: '30px',
  position: 'relative'
};

const titleStyle = {
  color: 'white',
  fontSize: '32px',
  fontWeight: '700',
  marginBottom: '8px',
  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
  background: 'linear-gradient(90deg, #fff, #e0e0e0)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent'
};

const subtitleStyle = {
  color: 'rgba(255,255,255,0.8)',
  fontSize: '14px',
  fontWeight: '500',
  letterSpacing: '0.5px'
};

const inputGroupStyle = {
  marginBottom: '25px',
  position: 'relative'
};

const inputContainerStyle = {
  position: 'relative',
  width: '100%'
};

const inputIconStyle = {
  position: 'absolute',
  left: '18px',
  top: '50%',
  transform: 'translateY(-50%)',
  color: 'rgba(255,255,255,0.7)',
  fontSize: '18px'
};

const inputStyle = {
  width: '100%',
  padding: '16px 20px 16px 50px',
  border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: '12px',
  fontSize: '15px',
  transition: 'all 0.3s ease',
  backgroundColor: 'rgba(255,255,255,0.08)',
  color: 'white',
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.05)'
};

const inputFocusStyle = {
  ...inputStyle,
  borderColor: 'rgba(255,255,255,0.4)',
  outline: 'none',
  boxShadow: '0 0 0 3px rgba(255,255,255,0.1)',
  backgroundColor: 'rgba(255,255,255,0.15)'
};

const buttonStyle = {
  width: '100%',
  padding: '18px',
  background: 'linear-gradient(90deg, rgba(255,255,255,0.3), rgba(255,255,255,0.2))',
  color: 'white',
  border: 'none',
  borderRadius: '12px',
  fontSize: '16px',
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
  backdropFilter: 'blur(5px)',
  position: 'relative',
  overflow: 'hidden'
};

const buttonHoverStyle = {
  ...buttonStyle,
  transform: 'translateY(-2px)',
  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
  background: 'linear-gradient(90deg, rgba(255,255,255,0.4), rgba(255,255,255,0.3))'
};

const buttonDisabledStyle = {
  ...buttonStyle,
  background: 'rgba(255,255,255,0.05)',
  cursor: 'not-allowed',
  transform: 'none',
  boxShadow: 'none'
};

const buttonRipple = {
  position: 'absolute',
  borderRadius: '50%',
  backgroundColor: 'rgba(255, 255, 255, 0.3)',
  transform: 'scale(0)',
  animation: 'ripple 600ms linear',
  pointerEvents: 'none'
};

const errorStyle = {
  color: '#ff6b6b',
  fontSize: '13px',
  marginTop: '8px',
  fontWeight: '500',
  textShadow: '0 1px 2px rgba(0,0,0,0.1)',
  display: 'flex',
  alignItems: 'center',
  gap: '6px'
};

const authMessageStyle = {
  color: '#ff6b6b',
  fontSize: '14px',
  marginBottom: '20px',
  textAlign: 'center',
  fontWeight: '500',
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: 'rgba(255, 107, 107, 0.1)',
  border: '1px solid rgba(255, 107, 107, 0.2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px'
};

const successMessageStyle = {
  color: '#51cf66',
  fontSize: '14px',
  marginBottom: '20px',
  textAlign: 'center',
  fontWeight: '500',
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: 'rgba(81, 207, 102, 0.1)',
  border: '1px solid rgba(81, 207, 102, 0.2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px'
};

const decorationStyle = {
  position: 'absolute',
  width: '300px',
  height: '300px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
  top: '-100px',
  right: '-100px',
  zIndex: -1
};

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    mobile: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [authSuccess, setAuthSuccess] = useState(null);
  const [isHoveringButton, setIsHoveringButton] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [ripples, setRipples] = useState([]);

  // Initialize only Database (no Auth needed)
  const db = getDatabase();

  useEffect(() => {
    // Fetch admin data from Firebase
    const adminRef = ref(db, 'admin');
    const unsubscribe = onValue(adminRef, (snapshot) => {
      const data = snapshot.val();
      setAdminData(data);
    });

    return () => unsubscribe();
  }, [db]);

  const validateMobile = (mobile) => {
    const re = /^[0-9]{10}$/;
    return re.test(mobile);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!validateMobile(formData.mobile)) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number';
    }
    
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const createRipple = (e) => {
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;
    
    const newRipple = {
      id: Date.now(),
      x,
      y,
      size
    };
    
    setRipples(prev => [...prev, newRipple]);
    
    setTimeout(() => {
      setRipples(prev => prev.filter(r => r.id !== newRipple.id));
    }, 600);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    setAuthError(null);
    setAuthSuccess(null);
    
    try {
      // Verify against stored admin credentials
      if (!adminData || formData.mobile !== adminData.mobile || formData.password !== adminData.password) {
        throw new Error('Invalid mobile number or password');
      }
      
      setAuthSuccess('Login successful!');
      
      // Set both localStorage and context state
      localStorage.setItem('isAuthenticated', 'true');
      login(); // Call the login function from your auth context
      
      // Redirect to dashboard or previous location
      const to = location.state?.from?.pathname || '/dashboard';
      setTimeout(() => navigate(to, { replace: true }), 2000);
    } catch (error) {
      console.error('Authentication error:', error);
      setAuthError(error.message || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={containerStyle}>
      {/* Floating background elements */}
      <div style={floatingElements}>
        <div style={floatingElement(300, 20, 10, '#667eea', 40)}></div>
        <div style={floatingElement(200, 70, 80, '#764ba2', 30)}></div>
        <div style={floatingElement(150, 40, 70, '#ffffff', 20)}></div>
      </div>

      <motion.div 
        style={formContainerStyle}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div style={formStyle}>
          <div style={decorationStyle}></div>
          
          <div style={headerStyle}>
            <h2 style={titleStyle}>Admin Login</h2>
            <p style={subtitleStyle}>Welcome back! Please enter your credentials</p>
          </div>

          {authError && (
            <motion.div 
              style={authMessageStyle}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <FiUser /> {authError}
            </motion.div>
          )}
          
          {authSuccess && (
            <motion.div 
              style={successMessageStyle}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <FiUser /> {authSuccess}
            </motion.div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={inputGroupStyle}>
              <motion.div 
                style={inputContainerStyle}
                whileHover={{ scale: 1.02 }}
              >
                <div style={inputIconStyle}>
                  <FiSmartphone />
                </div>
                <input
                  type="text"
                  name="mobile"
                  placeholder="Mobile Number"
                  value={formData.mobile}
                  onChange={handleChange}
                  style={errors.mobile ? inputFocusStyle : inputStyle}
                />
              </motion.div>
              {errors.mobile && (
                <motion.div 
                  style={errorStyle}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <FiUser size={14} /> {errors.mobile}
                </motion.div>
              )}
            </div>

            <div style={inputGroupStyle}>
              <motion.div 
                style={inputContainerStyle}
                whileHover={{ scale: 1.02 }}
              >
                <div style={inputIconStyle}>
                  <FiLock />
                </div>
                <input
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  style={errors.password ? inputFocusStyle : inputStyle}
                />
              </motion.div>
              {errors.password && (
                <motion.div 
                  style={errorStyle}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <FiLock size={14} /> {errors.password}
                </motion.div>
              )}
            </div>

            <motion.button
              type="submit"
              style={loading ? buttonDisabledStyle : (isHoveringButton ? buttonHoverStyle : buttonStyle)}
              onMouseEnter={() => setIsHoveringButton(true)}
              onMouseLeave={() => setIsHoveringButton(false)}
              disabled={loading}
              onClick={createRipple}
              whileTap={!loading ? { scale: 0.98 } : {}}
            >
              {loading ? 'Logging in...' : 'Login'}
              {ripples.map(ripple => (
                <span 
                  key={ripple.id}
                  style={{
                    ...buttonRipple,
                    left: ripple.x,
                    top: ripple.y,
                    width: ripple.size,
                    height: ripple.size,
                  }}
                />
              ))}
            </motion.button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;